"use strict";

import { Application } from "../../main/application.js";
import { Time } from "../../systems/core/time.js";
import { Mouse } from "../../systems/core/mouse.js";
import { UIContainer } from "../uiContainer.js";
import { UISprite } from "../uiSprite.js";
import { UINineSliceSprite } from "../uiNineSliceSprite.js";
import { UIButton } from "./uiButton.js";
import { UIStatesButton } from "./uiButton.js";
import { UIText } from "../uiText.js";
import { Coroutine } from "../../systems/secondary/coroutine.js";
import { Vector2 } from "../../math/vectors.js";

export class UIDropdown extends UIContainer {
    constructor(config) {
        let _onInitialize = config.onInitialize;
        config.onInitialize = undefined;

        super(config);

        this._direction = (config.direction ?? "down") == "up" ? 1 : 0;
        this.onExpanded = config.onExpanded;
        this.onRetracted = config.onRetracted;
        this.onOver = config.onOver;
        this.onOut = config.onOut;
        this.onSelected = config.onSelected;

        // Visual configs
        this._mainButtonConfig = config.mainButton;
        this._backgroundConfig = config.background;
        this._valueButtonConfig = config.valueButton;
        this._valueButtonConfig.height = this._valueButtonConfig.height ??
            this.calculatedRect.size.height;
        this._indicatorConfig = config.indicator;

        this.expandSpeed = config.expandSpeed ?? 5;
        this.retractSpeed = config.retractSpeed ?? 10;
        this._opened = false;
        this.animationCoroutine = null;

        // Forward Layer & Value Anchor
        this._forwardLayer = config.forwardLayer;

        this.valueAnchor = new SimpleEngine.UIContainer({
            rect: {
                position: this._getValueAnchorRect(),
                size: {
                    width: this.calculatedRect.size.width,
                    height: 0
                },
            },
            parent: this._forwardLayer
        });

        // Value buttons creation
        this.values = config.values;
        this.valueCount = this.values.length;

        let valueContainerAnchor = this._direction;
        this.valueContainer = new UIContainer({
            rect: {
                size: {
                    width: this.calculatedRect.size.width,
                    height: this._valueButtonConfig.height * this.valueCount
                },
                anchorY: {
                    min: valueContainerAnchor,
                    max: valueContainerAnchor
                },
                pivot: { x: 0.5, y: 1 - valueContainerAnchor }
            },
            parent: this.valueAnchor,
            interactive: true
        });
        this.valueContainer.on(Application.main.input.out, () => {
            if (this.indicator === undefined) return;
            this.indicator.visible = false;
        });

        this.endY = this._direction == 0 ?
            this.valueContainer.calculatedRect.size.height + 1 :
            -this.valueContainer.calculatedRect.size.height - 1;
        this.valueContainer.rect.position.y = this.endY;
        this.valueContainer.updateNow();

        // Background creation
        if (this._backgroundConfig !== undefined) {
            if (this._backgroundConfig.cornerSize !== undefined) {
                this._background = new SimpleEngine.UINineSliceSprite({
                    textureID: this._backgroundConfig.textureID,
                    cornerSize: this._backgroundConfig.cornerSize,
                    rect: {
                        anchorX: { min: 0, max: 1 },
                        anchorY: { min: 0, max: 1 }
                    },
                    parent: this.valueContainer
                });
            } else {
                this._background = new SimpleEngine.UISprite({
                    textureID: this._backgroundConfig.textureID,
                    rect: {
                        anchorX: { min: 0, max: 1 },
                        anchorY: { min: 0, max: 1 }
                    },
                    parent: this.valueContainer
                });
            }
        }

        // Indicator creation
        if (this._indicatorConfig !== undefined) {
            let offsetSize = this._indicatorConfig.offsetSize;
            if (offsetSize === undefined) {
                offsetSize = { width: 0, height: 0 }
            }

            if (this._indicatorConfig.cornerSize !== undefined) {
                this.indicator = new UINineSliceSprite({
                    textureID: this._indicatorConfig.textureID,
                    cornerSize: this._indicatorConfig.cornerSize,
                    rect: {
                        size: {
                            width: this.calculatedRect.size.width + offsetSize.width,
                            height: this._valueButtonConfig.height + offsetSize.height,
                        }
                    },
                    parent: this.valueContainer,
                    visible: false
                });
            } else {
                this.indicator = new UISprite({
                    textureID: this._indicatorConfig.textureID,
                    rect: {
                        size: {
                            width: this.calculatedRect.size.width + offsetSize.width,
                            height: this._valueButtonConfig.height + offsetSize.height,
                        }
                    },
                    parent: this.valueContainer,
                    visible: false
                });
            }
        }

        // Value mask creation
        this._valueMask = new UISprite({
            rect: {
                ...this.valueContainer.rect,
                position: { x: 0, y: 0}
            },
            parent: this.valueAnchor
        });
        this.valueContainer.mask = this._valueMask;
        if (config.debug) {
            this._valueMask.alpha = 0.3;
            this.valueContainer.mask = undefined;
        }

        // Value objects creation
        let valueOffsetY = -this._valueButtonConfig.height / 2;
        this.indicator.rect.position.y = valueOffsetY;

        this.valueObjects = [];
        for (let i = 0; i < this.valueCount; i++) {
            let stepY = 1 - i / this.valueCount;

            let object = new SimpleEngine.UIContainer({
                rect: {
                    position: { x: 0, y: valueOffsetY },
                    anchorY: { min: stepY, max: stepY },
                    size: {
                        width: this.calculatedRect.size.width,
                        height: this._valueButtonConfig.height
                    }
                },
                parent: this.valueContainer
            });

            object.button = new UIButton({
                id: i,
                textureID: this._valueButtonConfig.textureID,
                cornerSize: this._valueButtonConfig.cornerSize,
                rect: {
                    anchorX: { min: 0, max: 1 },
                    anchorY: { min: 0, max: 1 }
                },
                parent: object,
                onPointerOver: id => {
                    if (this.indicator !== undefined) {
                        this.indicator.rect.anchorY.min = 
                            this.valueObjects[id].rect.anchorY.min;
                        this.indicator.rect.anchorY.max = 
                            this.valueObjects[id].rect.anchorY.max;
                        this.indicator.visible = true;
                        this.indicator.updateNow();
                    }
                    this.onOver?.(object, id);
                },
                onPointerOut: id => {
                    this.onOut?.(object, id);
                },
                onPointerDown: id => {
                    if (this.animationCoroutine != null) return;
    
                    this.select(id);
                    this.retract();
                }
            });

            if (this._valueButtonConfig.textureID === undefined) {
                object.button.renderable = false;
            }
            
            object.label = new UIText({
                text: this.values[i].toString(),
                fontProperties: this._valueButtonConfig.fontProperties,
                parent: object
            });
            this.valueObjects.push(object);
        }

        // Main button creation
        this.mainButton = new UIStatesButton({
            textureID: this._mainButtonConfig.textureID,
            cornerSize: this._mainButtonConfig.cornerSize,
            rect: {
                anchorX: { min: 0, max: 1 },
                anchorY: { min: 0, max: 1 }
            },
            parent: this,
            onPointerOverState: this._mainButtonConfig.onPointerOverState,
            onPointerDownState: this._mainButtonConfig.onPointerDownState,
            onPointerUpState: this._mainButtonConfig.onPointerUpState,
            onPointerOutState: this._mainButtonConfig.onPointerOutState,
            onPointerUpOutsideState: this._mainButtonConfig.onPointerUpOutsideState,
            onPointerInactiveState: this._mainButtonConfig.onPointerInactiveState,
            onPointerDown: () => {
                if (this._opened) {
                    this.retract();
                    this.onRetracted?.();
                } else {
                    this.expand();
                    this.onExpanded?.();
                }
            }
        });
        this.valueLabel = new UIText({
            text: "-",
            fontProperties: this._mainButtonConfig.fontProperties,
            parent: this.mainButton
        });

        this.selectedIndex = config.selected ?? 0;
        this.select(this.selectedIndex, true);

        this._focusChecker = {
            position: { x: 0, y: 0 },
            size: { width: 0, height: 0 }
        };
        this.clickOutsideListener = (options) => {
            if (this.animationCoroutine != null ||
                (options.button !== undefined && options.button != 0)) return;

            this._focusChecker.size.width = this._valueMask._width;
            this._focusChecker.size.height = this._valueMask._height;
            this._focusChecker.position = this._valueMask.getRealWorldPosition();
            this._focusChecker.position.y += (this._direction == 0 ? 
                this._focusChecker.size.height : -this._focusChecker.size.height) / 2;

            let mainButtonHeight = this.transformToUI(this.calculatedRect.size.height);
            this._focusChecker.size.height += mainButtonHeight;
            this._focusChecker.position.y += (this._direction == 0 ?
                -mainButtonHeight : mainButtonHeight) / 2;
    
            let pointer = options.button === undefined ? {
                x: options.touches[0].clientX,
                y: options.touches[0].clientY
            } : {
                x: options.x,
                y: options.y
            };
    
            if (pointer.x >= this._focusChecker.position.x - this._focusChecker.size.width / 2 &&
                pointer.y >= this._focusChecker.position.y - this._focusChecker.size.height / 2 &&
                pointer.x <= this._focusChecker.position.x + this._focusChecker.size.width / 2 &&
                pointer.y <= this._focusChecker.position.y + this._focusChecker.size.height / 2) return;

            this.retract();
        }

        _onInitialize?.bind(this)(config);

        this.updateNow();
    }

    get opened() {
        return this._opened;
    }

    set direction(value) {
        let dir = 0;
        if (typeof value === "string") {
            dir = value == "up" ? 1 : 0;
        } else if (typeof value === "number") {
            dir = value == 1 ? 1 : 0;
        }

        this._setDirection(dir);
    }

    enable() {
        this.mainButton.enable();
    }
    disable() {
        this.mainButton.disable();
    }

    show(enable) {
        this.mainButton.show(enable);
    }
    hide() {
        this.mainButton.hide();
    }
    
    _setDirection(value) {
        this._direction = value;
        this._hardUpdate();
    }

    expand() {
        if (this._opened) return;

        this._opened = true;

        if (this.animationCoroutine != null) {
            Coroutine.stop(this.animationCoroutine);
        }

        Mouse.registerGlobalEvent(Application.main.input.down, this.clickOutsideListener, true);

        this.animationCoroutine = Coroutine.start(new Coroutine(this._expand()));
    }
    *_expand() {
        let delta = 0;

        while (delta < 1) {
            delta = Math.min(delta + Time.deltaTime * this.expandSpeed, 1);

            this.valueContainer.rect.position.y = Math.lerp(this.endY, 0, delta);
            this.valueContainer.updateNow();

            yield null;
        }

        this.animationCoroutine = null;
    }

    retract(fast) {
        if (!this._opened) return;

        this._opened = false;

        if (this.animationCoroutine != null) {
            Coroutine.stop(this.animationCoroutine);
        }

        Mouse.unregisterGlobalEvent(Application.main.input.down, this.clickOutsideListener, true);

        if (fast) {
            this.valueContainer.rect.position.y = this.endY;
            this.valueContainer.updateNow();
            return;
        }

        this.animationCoroutine = Coroutine.start(new Coroutine(this._retract()));
    }
    *_retract(delta = 0) {
        while (delta < 1) {
            delta = Math.min(delta + Time.deltaTime * this.retractSpeed, 1);

            this.valueContainer.rect.position.y = Math.lerp(0, this.endY, delta);
            this.valueContainer.updateNow();

            yield null;
        }

        this.animationCoroutine = null;
    }

    select(index,silent) {
        this.selectedIndex = index;
        this.valueLabel.setText(this.values[index].toString());
        if (silent) return;
        this.onSelected?.(this.selectedIndex, this.valueLabel);
    }

    updateNow() {
        super.updateNow();
        if (this.valueAnchor === undefined) return;
        this.valueAnchor.updateNow();
    }

    _hardUpdate() {
        this.valueAnchor.setRect({
            position: this._getValueAnchorRect(),
            size: {
                width: this.calculatedRect.size.width,
                height: 0
            },
        });

        for (let i = 0; i < this.valueObjects.length; i++) {
            this.valueObjects[i].rect.size.width = this.calculatedRect.size.width;
            this.valueObjects[i].rect.size.height = this._valueButtonConfig.height;
        }

        let valueContainerAnchor = this._direction;
        this.valueContainer.rect.size.width = this.calculatedRect.size.width;
        this.valueContainer.rect.size.height = this._valueButtonConfig.height * this.valueCount;
        this.valueContainer.rect.anchorY.min = valueContainerAnchor;
        this.valueContainer.rect.anchorY.max = valueContainerAnchor;
        this.valueContainer.rect.pivot.y = 1 - valueContainerAnchor;
        this.valueContainer.updateNow();

        this.endY = this._direction == 0 ?
            this.valueContainer.calculatedRect.size.height + 1 :
            -this.valueContainer.calculatedRect.size.height - 1;
        this.valueContainer.rect.position.y = this.endY;
        this.valueContainer.updateNow();

        this._valueMask.rect = {
            ...this.valueContainer.rect,
            position: { x: 0, y: 0 }
        };
        this._valueMask.updateNow();
    }
    _getValueAnchorRect() {
        let tempPos = Vector2.subtract(
            this._forwardLayer.getRealWorldPosition(),
            this.getRealWorldPosition());
        tempPos.y = -tempPos.y;
        tempPos = this.transformFromUI(tempPos);

        if (this._direction == 1) {
            tempPos.y += this.calculatedRect.size.height -
                this.calculatedRect.size.height * this.rect.pivot.y;
        } else {
            tempPos.y -= this.calculatedRect.size.height * this.rect.pivot.y;
        }
        return tempPos;
    }

    onOrientationChanged() {
        this.retract(true);
        this._hardUpdate();
        super.onOrientationChanged();
    }
}